
import React, { useContext, useEffect, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Divider,
  Typography,
  MenuItem,
  Avatar,
  ButtonBase,
  Popover,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { logoutStorage } from 'src/utils/isAuthenticated';
import { LOGOUT } from 'src/context/constatnts';
import toast from 'react-hot-toast';
import { AuthContext } from 'src/context/AuthContext';
import config from 'src/config';
import Iconify from '../../../components/iconify';

export default function AccountPopover() {
  const { state, dispatch } = useContext(AuthContext);
  const [open, setOpen] = useState(null);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const user = state?.user;

  const fullName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
    'My Account';

  const email = user?.email || '';

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  useEffect(() => {
    try {
      const storedItems = JSON.parse(
        localStorage.getItem(config.PROJECT_NAME)
      );

      if (storedItems) {
        setItems(storedItems);
      }
    } catch (error) {
      console.error('Unable to read stored account items:', error);
    }
  }, []);

  const logout = () => {
    handleClose();
    logoutStorage();
    dispatch({ type: LOGOUT });
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  const profile = () => {
    handleClose();
    navigate('/profile/edit', { replace: true });
  };

  const settings = () => {
    handleClose();
    navigate('/settings/edit', { replace: true });
  };

  return (
    <>
      {/* Header account button: email is hidden here */}
      <ButtonBase
        onClick={handleOpen}
        sx={{
          pl: 1,
          py: 0.5,
          pr: 1,
          borderRadius: 1,
          transition: (theme) =>
            theme.transitions.create('background-color'),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
          },
        }}
      >
        <Avatar
          alt={fullName}
          sx={{
            width: 36,
            height: 36,
            mr: 1,
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {fullName.charAt(0).toUpperCase()}
        </Avatar>

        <Box
          sx={{
            display: { xs: 'none', sm: 'block' },
            maxWidth: 160,
            textAlign: 'left',
          }}
        >
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ maxWidth: 160 }}
          >
            {fullName}
          </Typography>
        </Box>

        <Iconify
          icon={
            open
              ? 'eva:arrow-ios-upward-fill'
              : 'eva:arrow-ios-downward-fill'
          }
          sx={{ ml: 0.5, width: 18, height: 18 }}
        />
      </ButtonBase>

      {/* Dropdown: email remains visible here */}
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 0,
              mt: 1.5,
              width: 240,
              borderRadius: 2,
              boxShadow: (theme) =>
                theme.customShadows?.z20,
            },
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {fullName}
          </Typography>

          {email && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: 'text.secondary',
                overflowWrap: 'anywhere',
              }}
            >
              {email}
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <MenuItem onClick={profile} sx={{ mb: 0.5 }}>
            <Iconify
              icon="eva:person-outline"
              sx={{ mr: 1.5, width: 20, height: 20 }}
            />
            Profile
          </MenuItem>

          <MenuItem onClick={settings} sx={{ mb: 0.5 }}>
            <Iconify
              icon="eva:settings-2-outline"
              sx={{ mr: 1.5, width: 20, height: 20 }}
            />
            Settings
          </MenuItem>

          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

          <MenuItem
            onClick={logout}
            sx={{ color: 'error.main' }}
          >
            <Iconify
              icon="eva:log-out-outline"
              sx={{ mr: 1.5, width: 20, height: 20 }}
            />
            Logout
          </MenuItem>
        </Box>
      </Popover>
    </>
  );
}
